// ═══════════════════════════════════════════════════════════════
// Outstanding — cross-customer list of every invoiced-but-not-paid
// job, sorted oldest-invoiced first so chasing-the-money is a
// natural top-down read. Reachable via the "Outstanding" stat card
// on Home (clickable). Each row gets inline Text Invoice / Email
// Invoice / Mark Paid actions and a tap-through to the customer.
//
// "Outstanding" = status === 'invoiced' (sent invoice, no payment
// confirmation yet). Closed-not-invoiced cash deals don't appear
// here — they live on the customer's job page with the "Closed —
// needs payment" chip until you Mark Paid or send an invoice.
// ═══════════════════════════════════════════════════════════════
import React, { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, MessageSquare, Mail, Check, AlertCircle, ExternalLink,
} from 'lucide-react';
import { jobsCol } from './database.js';
import { updateOne, markJobAsPaid } from './lib/db-crud.js';
import { useUserData } from './lib/use-user-data.js';
import {
  formatPrice, formatPhone, openSms, openMailto, daysSince, daysAgoLabel, toDate,
  jobsForActiveCustomers,
} from './lib/job-helpers.js';
import { vehicleLabel } from './Vehicles.jsx';
import { formatInvoiceNumber } from './lib/job-number.js';

export default function Outstanding() {
  const { jobs: allJobs, customers, allCustomers, vehicles, ready } = useUserData();

  // Archived customers drop off this list with everything else of
  // theirs. Restore brings the whole record back untouched.
  const jobs = useMemo(() => jobsForActiveCustomers(allJobs, allCustomers), [allJobs, allCustomers]);
  const navigate = useNavigate();

  const customerById = useMemo(
    () => Object.fromEntries(customers.map((c) => [c.id, c])),
    [customers]
  );
  const vehicleById = useMemo(
    () => Object.fromEntries(vehicles.map((v) => [v.id, v])),
    [vehicles]
  );

  // Oldest invoice first — that's the one most likely to be forgotten.
  const outstanding = useMemo(() => {
    return jobs
      .filter((j) => j.status === 'invoiced')
      .map((j) => ({ ...j, _invoicedAt: toDate(j.invoicedAt) }))
      .sort((a, b) => {
        const aT = a._invoicedAt?.getTime() ?? 0;
        const bT = b._invoicedAt?.getTime() ?? 0;
        return aT - bT;
      });
  }, [jobs]);

  const totalOwed = useMemo(
    () => outstanding.reduce((sum, j) => sum + (Number(j.totalPrice) || 0), 0),
    [outstanding]
  );

  async function handleMarkPaid(j) {
    const customer = customerById[j.customerId];
    if (!confirm(`Mark this invoice paid?\n\n${customer?.name || 'Customer'} — ${formatPrice(j.totalPrice)}\n"${j.description}"`)) return;
    try {
      await markJobAsPaid(jobsCol(), j, 'mark-paid');
    } catch (e) {
      alert(`Could not mark paid: ${e.message}`);
    }
  }

  function handleResend(j, channel) {
    const customer = customerById[j.customerId];
    if (!customer) { alert('Customer info missing.'); return; }
    if (channel === 'email' && !customer.email) {
      alert('No email on file. Resend by text or add an email on the customer page.');
      return;
    }
    if (!j.invoiceShareId) {
      alert('No invoice link on file. Open the customer\'s job page to send the invoice fresh.');
      return;
    }
    const invoiceUrl = `${window.location.origin}/invoice/${j.invoiceShareId}`;
    const body = `Reminder — invoice from your mechanic:\n\n${invoiceUrl}`;
    if (channel === 'email') {
      openMailto(customer.email, 'Invoice reminder', body);
    } else {
      openSms(customer.phone, body);
    }
  }

  return (
    <div>
      <div className="screen-header">
        <div>
          <Link to="/" className="btn-link" style={{ marginBottom: 4 }}>
            <ArrowLeft size={14} /> Home
          </Link>
          <h1>Outstanding invoices</h1>
          <p className="subtitle">
            {ready
              ? (outstanding.length === 0
                  ? 'Nothing outstanding — all invoices paid.'
                  : `${outstanding.length} unpaid · ${formatPrice(totalOwed)} owed`)
              : 'Loading…'}
          </p>
        </div>
      </div>

      {!ready && <p className="muted small">Loading outstanding invoices…</p>}

      {ready && outstanding.length === 0 && (
        <div className="empty-state">
          <Check size={32} color="var(--status-approved-fg)" strokeWidth={1.5} style={{ marginBottom: 8 }} />
          <p style={{ fontWeight: 600 }}>All caught up.</p>
          <p className="muted small">No invoices waiting on payment. Treat yourself.</p>
        </div>
      )}

      {ready && outstanding.length > 0 && (
        <ul className="outstanding-list">
          {outstanding.map((j) => {
            const customer  = customerById[j.customerId];
            const vehicle   = vehicleById[j.vehicleId];
            const ageDays   = daysSince(j._invoicedAt);
            const overdue   = ageDays != null && ageDays >= 14;
            return (
              <li key={j.id} className={`outstanding-row ${overdue ? 'overdue' : ''}`}>
                <button
                  type="button"
                  className="outstanding-row-body"
                  onClick={() => navigate(`/customers/${j.customerId}`)}
                  title="Open customer"
                >
                  <div className="outstanding-row-head">
                    <span className="outstanding-customer">
                      {customer?.name || '(deleted customer)'}
                      {formatInvoiceNumber(j.jobNumber) && (
                        <span className="doc-number mono">{formatInvoiceNumber(j.jobNumber)}</span>
                      )}
                    </span>
                    <span className="outstanding-amount mono">{formatPrice(j.totalPrice)}</span>
                  </div>
                  <div className="outstanding-row-meta">
                    <span className="outstanding-desc">{j.description}</span>
                    {vehicle && <span className="muted small"> · {vehicleLabel(vehicle)}</span>}
                  </div>
                  <div className="outstanding-row-foot">
                    {overdue && (
                      <span className="age-pill overdue-pill">
                        <AlertCircle size={11} style={{ verticalAlign: '-2px', marginRight: 3 }} />
                        {daysAgoLabel(ageDays, 'Invoiced')}
                      </span>
                    )}
                    {!overdue && ageDays != null && (
                      <span className="age-pill">{daysAgoLabel(ageDays, 'Invoiced')}</span>
                    )}
                    {customer?.phone && (
                      <span className="muted small">{formatPhone(customer.phone)}</span>
                    )}
                  </div>
                </button>
                <div className="outstanding-row-actions">
                  <button
                    type="button"
                    className="btn-link"
                    onClick={() => handleResend(j, 'text')}
                    title="Resend invoice via text"
                  >
                    <MessageSquare size={13} /> Text
                  </button>
                  {customer?.email && (
                    <button
                      type="button"
                      className="btn-link"
                      onClick={() => handleResend(j, 'email')}
                      title="Resend invoice via email"
                    >
                      <Mail size={13} /> Email
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn-link"
                    onClick={() => handleMarkPaid(j)}
                    title="Mark this invoice paid"
                  >
                    <Check size={13} /> Mark Paid
                  </button>
                  <Link
                    to={`/customers/${j.customerId}`}
                    className="btn-link"
                    title="Open customer page"
                  >
                    <ExternalLink size={13} />
                  </Link>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
